"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import type { WorksheetCommonStrings } from "@/components/tools/worksheets-math/common-strings";
import { t } from "@/lib/i18n/interpolate";
import {
  COLUMN_CHOICES,
  clampProblemCount,
  FONT_POINTS,
  type FontSize,
  type PageOrientation,
  type PaperSize,
  PROBLEM_COUNT_CHOICES,
  type ProblemLayout,
  worksheetPrintCss,
} from "@/lib/tool-kit/worksheets/layout";
import {
  DEFAULT_SEED,
  formatSeed,
  newSeed,
  parseSeed,
} from "@/lib/tool-kit/worksheets/random";

/* ----------------------------------------------------------------- settings */

export interface CommonSettings {
  paper: PaperSize;
  fontSize: FontSize;
  count: number;
  layout: ProblemLayout;
  /** 0 picks a column count from the width of the problems. */
  columns: number;
  title: string;
  header: boolean;
  numbered: boolean;
  answerKey: boolean;
}

export function defaultSettings(
  overrides: Partial<CommonSettings> = {}
): CommonSettings {
  return {
    paper: "letter",
    fontSize: "medium",
    count: 20,
    layout: "vertical",
    columns: 0,
    title: "",
    header: true,
    numbered: true,
    answerKey: true,
    ...overrides,
  };
}

/** Settings state with a patch-style updater. */
export function useCommonSettings(initial: CommonSettings) {
  const [settings, setSettings] = useState(initial);
  const update = (patch: Partial<CommonSettings>) =>
    setSettings((previous) => ({ ...previous, ...patch }));
  return [settings, update] as const;
}

/**
 * The worksheet's seed. The first render uses `DEFAULT_SEED` (the same on server and
 * client); a fresh one is drawn only inside the button's click handler.
 */
export function useWorksheetSeed() {
  const [seed, setSeed] = useState(DEFAULT_SEED);
  const [text, setText] = useState(String(DEFAULT_SEED));
  const parsed = parseSeed(text);
  return {
    seed,
    text,
    invalid: parsed === null,
    edit(value: string) {
      setText(value);
      const next = parseSeed(value);
      if (next !== null) {
        setSeed(next);
      }
    },
    regenerate() {
      const next = newSeed();
      setSeed(next);
      setText(String(next));
    },
  };
}

type Seed = ReturnType<typeof useWorksheetSeed>;

const SEED_FIELD_WIDTH = "max-w-[14rem]";

/* ----------------------------------------------------------------- controls */

export function CommonControls({
  idPrefix,
  strings,
  settings,
  onChange,
  seed,
  layouts = true,
  columns = true,
  countChoices = PROBLEM_COUNT_CHOICES,
}: {
  idPrefix: string;
  strings: WorksheetCommonStrings;
  settings: CommonSettings;
  onChange: (patch: Partial<CommonSettings>) => void;
  seed: Seed;
  layouts?: boolean;
  columns?: boolean;
  countChoices?: readonly number[];
}) {
  const counts = countChoices.includes(settings.count)
    ? countChoices
    : [...countChoices, settings.count].sort((a, b) => a - b);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          id={`${idPrefix}-count`}
          label={strings.countLabel}
          onChange={(value) =>
            onChange({ count: clampProblemCount(Number(value)) })
          }
          options={counts.map((value) => ({
            value: String(value),
            label: String(value),
          }))}
          value={String(settings.count)}
        />
        {layouts ? (
          <SelectField
            id={`${idPrefix}-layout`}
            label={strings.layoutLabel}
            onChange={(layout) => onChange({ layout })}
            options={(["vertical", "horizontal"] as const).map((value) => ({
              value,
              label: strings.layouts[value],
            }))}
            value={settings.layout}
          />
        ) : null}
        {columns ? (
          <SelectField
            id={`${idPrefix}-columns`}
            label={strings.columnsLabel}
            onChange={(value) => onChange({ columns: Number(value) })}
            options={[
              { value: "0", label: strings.columnsAuto },
              ...COLUMN_CHOICES.map((value) => ({
                value: String(value),
                label: String(value),
              })),
            ]}
            value={String(settings.columns)}
          />
        ) : null}
        <SelectField
          id={`${idPrefix}-font`}
          label={strings.fontSizeLabel}
          onChange={(fontSize) => onChange({ fontSize })}
          options={(["small", "medium", "large"] as const).map((value) => ({
            value,
            label: strings.fontSizes[value],
          }))}
          value={settings.fontSize}
        />
        <SelectField
          id={`${idPrefix}-paper`}
          label={strings.paperLabel}
          onChange={(paper) => onChange({ paper })}
          options={(["letter", "a4"] as const).map((value) => ({
            value,
            label: strings.papers[value],
          }))}
          value={settings.paper}
        />
      </div>
      <TextField
        hint={strings.titleHint}
        id={`${idPrefix}-title`}
        label={strings.titleLabel}
        onChange={(title) => onChange({ title })}
        value={settings.title}
      />
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={settings.header}
          label={strings.showHeader}
          onChange={(header) => onChange({ header })}
        />
        <Checkbox
          checked={settings.numbered}
          label={strings.numbered}
          onChange={(numbered) => onChange({ numbered })}
        />
        <Checkbox
          checked={settings.answerKey}
          label={strings.answerKey}
          onChange={(answerKey) => onChange({ answerKey })}
        />
      </div>
      <ActionBar idPrefix={idPrefix} seed={seed} strings={strings} />
    </div>
  );
}

/** The seed field, the New worksheet and Print buttons, and the printing hint. */
export function ActionBar({
  idPrefix,
  strings,
  seed,
  showSeed = true,
}: {
  idPrefix: string;
  strings: WorksheetCommonStrings;
  seed: Seed;
  /** False for sheets with nothing random to reproduce. */
  showSeed?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        {showSeed ? (
          <>
            <div className={`w-full ${SEED_FIELD_WIDTH}`}>
              <TextField
                hint={strings.seedHint}
                id={`${idPrefix}-seed`}
                label={strings.seedLabel}
                numeric
                onChange={seed.edit}
                value={seed.text}
              />
            </div>
            <button
              className={BUTTON_PRIMARY}
              onClick={seed.regenerate}
              type="button"
            >
              {strings.newWorksheet}
            </button>
          </>
        ) : null}
        <button
          className={showSeed ? BUTTON_SECONDARY : BUTTON_PRIMARY}
          onClick={() => window.print()}
          type="button"
        >
          {strings.print}
        </button>
      </div>
      {showSeed && seed.invalid ? (
        <ErrorNotice>{strings.seedInvalid}</ErrorNotice>
      ) : null}
      <p className={HINT}>{strings.printHint}</p>
    </div>
  );
}

export function UnmetNotice({
  unmet,
  strings,
}: {
  unmet: number;
  strings: WorksheetCommonStrings;
}) {
  return unmet > 0 ? <Notice>{strings.unmetNotice}</Notice> : null;
}

/* -------------------------------------------------------------------- sheet */

const SHEET =
  "ws-sheet mx-auto w-full max-w-[8.5in] rounded-sm border border-border bg-white p-6 text-neutral-900 shadow-sm sm:p-10";
const RULE = "border-neutral-900 border-b";

function HeaderLines({ strings }: { strings: WorksheetCommonStrings }) {
  return (
    <div className="mb-5 grid grid-cols-[2fr_1fr_1fr] gap-6 text-sm">
      {[strings.nameLabel, strings.dateLabel, strings.scoreLabel].map(
        (label) => (
          <div className={`${RULE} pb-0.5`} key={label}>
            {label}:
          </div>
        )
      )}
    </div>
  );
}

export function WorksheetSheets({
  settings,
  strings,
  title,
  seed,
  problems,
  answers,
  caption,
  showSeed = true,
  orientation = "portrait",
}: {
  settings: CommonSettings;
  strings: WorksheetCommonStrings;
  /** The title that will print (already defaulted). */
  title: string;
  seed: number;
  problems: ReactNode;
  /** The answer key's body; omitted when the key is off. */
  answers: ReactNode;
  /** Extra line under the title (for example the skill and settings). */
  caption?: string;
  /** False when the sheet has nothing random, so no worksheet number is printed. */
  showSeed?: boolean;
  /** The printed page's orientation; wide sheets (number lines) print in landscape. */
  orientation?: PageOrientation;
}) {
  const seedLine = showSeed
    ? t(strings.seedCaption, { seed: formatSeed(seed) })
    : "";
  return (
    <div className="space-y-6 print:space-y-0">
      <style>{worksheetPrintCss(settings.paper, orientation)}</style>
      <section
        aria-label={strings.previewLabel}
        className={`${SHEET} overflow-x-auto`}
        style={{ fontSize: `${FONT_POINTS[settings.fontSize]}pt` }}
      >
        <div className="mb-4 flex items-baseline justify-between gap-4 font-sans">
          <h2 className="font-semibold text-[16pt]">{title}</h2>
          <span className="text-[9pt] text-neutral-600">{seedLine}</span>
        </div>
        {caption ? (
          <p className="mb-3 text-[10pt] text-neutral-600">{caption}</p>
        ) : null}
        {settings.header ? <HeaderLines strings={strings} /> : null}
        {problems}
      </section>
      {settings.answerKey ? (
        <section
          aria-label={strings.keyLabel}
          className={`${SHEET} ws-key`}
          style={{ fontSize: "12pt" }}
        >
          <div className="mb-4 flex items-baseline justify-between gap-4 font-sans">
            <h2 className="font-semibold text-[16pt]">
              {title} — {strings.answerKeyTitle}
            </h2>
            <span className="text-[9pt] text-neutral-600">{seedLine}</span>
          </div>
          {answers}
        </section>
      ) : null}
    </div>
  );
}

/** Problems laid out in `columns` equal columns. */
export function ProblemGrid({
  columns,
  children,
  gap = "gap-x-8 gap-y-8",
}: {
  columns: number;
  children: ReactNode;
  gap?: string;
}) {
  return (
    <div
      className={`grid ${gap}`}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

/** A problem's number badge and body, kept on one page when printed. */
export function ProblemCell({
  number,
  children,
}: {
  number: number | null;
  children: ReactNode;
}) {
  return (
    <div className="ws-avoid-break flex items-start gap-2">
      {number === null ? null : (
        <span className="min-w-[1.6em] font-sans text-[0.55em] text-neutral-600 leading-[2.4]">
          {number}.
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** The answer key body: numbered answers in columns. */
export function AnswerList({
  entries,
  columns,
}: {
  entries: readonly string[];
  columns: number;
}) {
  return (
    <ol
      className="grid gap-x-8 gap-y-2 font-mono"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {entries.map((entry, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: answers are positional and never reordered
        <li className="ws-avoid-break" key={index}>
          <span className="mr-2 text-neutral-600">{index + 1}.</span>
          {entry}
        </li>
      ))}
    </ol>
  );
}
